/**
 * FrameLayoutCard Component
 * 
 * Individual frame layout card displayed in the FrameLayoutSelector.
 * Shows frame thumbnail, name, and slot count with active/inactive states.
 * 
 * Requirements: 2.2, 2.4, 2.5
 */

import { useState } from 'react';
import type { LocalFrame } from '../../../lib/db/schema';
import { LoadingSkeleton } from '../../../components/LoadingSkeleton';

export interface FrameLayoutCardProps {
  /** Frame data to display */
  frame: LocalFrame;
  /** Whether this frame is currently selected */
  isSelected: boolean;
  /** Callback when frame is clicked */
  onSelect: (frameId: string) => void;
  /** When true, show only image (and optional categoryLabel); no name, slot count, or paper badge */
  compact?: boolean;
  /** Optional category label shown above image in compact mode */
  categoryLabel?: string;
}

/**
 * Get slot count based on paper type
 * For MVP, we infer slot count from paper type
 */
function getSlotCount(paperType: 'collage_4x6' | 'strip'): number {
  return paperType === 'collage_4x6' ? 4 : 2;
}

/**
 * FrameLayoutCard component - Displays individual frame option
 */
export function FrameLayoutCard({
  frame,
  isSelected,
  onSelect,
  compact = false,
  categoryLabel,
}: FrameLayoutCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const slotCount = getSlotCount(frame.paperType);

  // Create object URL for thumbnail or full image
  const imageUrl = frame.thumbnailBlob
    ? URL.createObjectURL(frame.thumbnailBlob)
    : URL.createObjectURL(frame.imageBlob);

  const handleClick = () => {
    onSelect(frame.frameId);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(frame.frameId);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`
        relative
        flex-shrink-0
        rounded-lg
        overflow-hidden
        transition-all
        duration-300
        ease-in-out
        cursor-pointer
        border-4
        focus:outline-none
        focus:ring-4
        focus:ring-yellow-500
        focus:ring-offset-2
        ${compact ? 'w-[140px]' : 'w-full'}
        ${isSelected
          ? 'border-yellow-500 bg-yellow-50 shadow-xl scale-105 transform'
          : 'border-transparent hover:border-yellow-400 hover:shadow-lg hover:scale-102'
        }
      `}
      data-testid={`frame-card-${frame.frameId}`}
      data-frame-id={frame.frameId}
      data-is-selected={isSelected}
      aria-pressed={isSelected}
      aria-label={`${frame.name} frame with ${slotCount} photo slots${isSelected ? ', currently selected' : ''}`}
    >
      {compact && categoryLabel && (
        <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 truncate px-1">
          {categoryLabel}
        </div>
      )}
      {/* Frame thumbnail image */}
      <div className={`relative bg-gray-100 ${compact ? 'aspect-[3/4] w-full' : 'w-full aspect-[3/4]'}`}>
        {!imageLoaded && (
          <LoadingSkeleton aspectRatio="3/4" className="absolute inset-0" />
        )}
        <img
          src={imageUrl}
          alt={`${frame.name} frame preview`}
          className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
          data-testid={`frame-thumbnail-${frame.frameId}`}
        />
        {/* Selected indicator overlay */}
        {isSelected && (
          <div className="absolute inset-0 bg-yellow-400 bg-opacity-20 pointer-events-none flex items-center justify-center transition-all duration-300">
            <div className="bg-yellow-500 text-white rounded-full p-2 shadow-lg transform scale-100 animate-bounce">
              <svg
                className="w-6 h-6"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        )}
      </div>
      {/* Frame info section (hidden in compact mode) */}
      {!compact && (
        <div
          className={`
        p-3
        transition-colors
        duration-300
        ${isSelected ? 'bg-yellow-50' : 'bg-white'}
      `}
        >
          <h3
            className={`
            text-sm
            font-semibold
            truncate
            ${isSelected ? 'text-yellow-900' : 'text-gray-900'}
          `}
            data-testid={`frame-name-${frame.frameId}`}
          >
            {frame.name}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <svg
              className={`w-4 h-4 ${isSelected ? 'text-yellow-600' : 'text-gray-500'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span
              className={`
            text-xs
            ${isSelected ? 'text-yellow-700 font-medium' : 'text-gray-600'}
          `}
              data-testid={`frame-slot-count-${frame.frameId}`}
            >
              {slotCount} {slotCount === 1 ? 'photo' : 'photos'}
            </span>
          </div>
          <div className="mt-2">
            <span
              className={`
            inline-block
            text-xs
            px-2
            py-1
            rounded-full
            ${isSelected
                ? 'bg-yellow-200 text-yellow-800'
                : 'bg-gray-200 text-gray-700'
              }
          `}
            >
              {frame.paperType === 'collage_4x6' ? '4x6 Collage' : 'Strip'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
